import { Session } from "../SessionModel";

export enum NormalizedState {
	notSupported,
	disabled,
	enabled,
	alwaysOn,
}

/**
 * Determines the normalized-value state for the session and the related UI behavior.
 *
 * Rules:
 * - If normalized data is not available, normalization is not supported and the toggle is hidden.
 * - In Combine mode:
 *   - `Outdoor`: normalization is always off and the toggle is disabled.
 *   - Any non-`Outdoor` location (for example `Indoor`, `Net`, or `Screen`):
 *     normalization is always on and the toggle is disabled.
 * - In non-Combine mode:
 *   - Any non-`Outdoor` location (for example `Indoor`, `Net`, or `Screen`):
 *     normalization is always on and the toggle is disabled.
 *   - `Outdoor` without `normalizedBallType`: normalization is always off and the toggle is disabled.
 *   - `Outdoor` with `normalizedBallType`: normalization is available and the user can change the toggle.
 *
 * @param session Session
 * @returns `NormalizedState`
 */
export const GetNormalizedState = (session: Session) => {
	// Check every shot, not just the first: some sessions have a leading shot
	// without normalizedValues while later shots do have them. Keying off
	// shots[0] alone misclassified such sessions as notSupported, hiding the
	// toggle and forcing normalization OFF (inconsistent with the Golf App). KWSA-28.
	const hasNormalizedValues = Boolean(
		session.shots?.some(shot => shot?.normalizedValues !== undefined && shot?.normalizedValues !== null),
	);

	// No normalized data → not supported, disable toggle
	if (!hasNormalizedValues) {
		return NormalizedState.notSupported;
	}

	const isCombine = session.drillTargets && session.drillTargets?.length > 0;
	const isOutdoor = session.location === "Outdoor";

	// Combine mode: always fixed (toggle disabled)
	// - Outdoor → always off
	// - Indoor  → always on
	if (isCombine) {
		return isOutdoor ? NormalizedState.disabled : NormalizedState.alwaysOn;
	}

	// Non-Combine, Indoor (any location that is not Outdoor): always on (toggle disabled)
	if (!isOutdoor) {
		return NormalizedState.alwaysOn;
	}

	// Outdoor without ball type configured → always off (toggle disabled)
	if (session.normalizedBallType == null) {
		return NormalizedState.disabled;
	}

	// Outdoor with ball type → user can toggle
	return NormalizedState.enabled;
};

export const ShowNormalized = (session: Session) => {
	switch (GetNormalizedState(session)) {
		case NormalizedState.notSupported:
		case NormalizedState.disabled:
			return false;
		case NormalizedState.enabled:
		case NormalizedState.alwaysOn:
			return true;
	}
};
